import { useState } from 'react';
import { DiscountCodeInput } from '../cart/DiscountCodeInput';
import type { CheckoutCart, DiscountInfo } from '../../types/checkout';

interface DiscountPreviewData {
  code: string;
  discount: number;
  discountPercentage: number;
  discountableItems: string[];
  discountInfo: DiscountInfo;
}

interface CartSummaryProps {
  cart: CheckoutCart;
  productsPageUrl: string;
  // Discount management props (optional - only passed when discount input is enabled)
  allowsDiscountCode?: boolean;
  discountError?: string | null;
  isLoadingDiscount?: boolean;
  onPreviewDiscount?: (code: string) => Promise<void>;
  onConfirmDiscount?: () => Promise<void>;
  onCancelPreview?: () => void;
  onApplyDiscount?: (code: string) => Promise<void>;
  onClearDiscount?: () => Promise<void>;
  previewData?: DiscountPreviewData | null;
  /**
   * Mercado Pago surcharge on the wholesale part of the cart, already resolved
   * against the selected payment method by the caller (0 when it does not
   * apply). Passed in rather than derived here because this component does not
   * know which payment method is selected.
   */
  wholesaleSurcharge?: number;
}

export function CartSummary({
  cart,
  productsPageUrl,
  allowsDiscountCode = false,
  discountError = null,
  isLoadingDiscount = false,
  onPreviewDiscount,
  onConfirmDiscount,
  onCancelPreview,
  onApplyDiscount,
  onClearDiscount,
  previewData,
  wholesaleSurcharge = 0,
}: CartSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (cart.isEmpty) {
    return (
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
        <div className="mb-4 text-gray-300">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-semibold text-gray-800">Tu carrito esta vacio</h3>
        <p className="mb-5 text-sm text-gray-500">Agrega productos para continuar</p>
        <a href={productsPageUrl} className="inline-block rounded-lg bg-green-500 px-6 py-3 font-semibold text-white hover:bg-green-600">
          Ver productos
        </a>
      </div>
    );
  }

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  // Use subtotal + delivery if available, otherwise total + delivery
  const grandTotal = (cart.subtotal != null
    ? (cart.subtotal - (cart.discount || 0) - (cart.subscriptionDiscount || 0) - (cart.replacementCreditDiscount || 0) + cart.deliveryPrice)
    : (cart.total + cart.deliveryPrice)) + wholesaleSurcharge;

  const showDiscountInput = allowsDiscountCode && onPreviewDiscount && onConfirmDiscount && onCancelPreview && onApplyDiscount && onClearDiscount;

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2.5 px-4 py-3.5"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="text-green-500">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        </span>
        <span className="flex-1 text-left text-[15px] font-semibold text-gray-800">
          Tu pedido ({itemCount} {itemCount === 1 ? 'producto' : 'productos'})
        </span>
        <span className="text-[15px] font-semibold text-green-500">
          ${grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
        </span>
        <span className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 px-4 py-4">
          <div className="mb-4">
            {cart.items.map((item) => (
              <div key={`${item.id}-${item.format_id}`} className="flex items-start gap-2.5 border-b border-gray-50 py-2 last:border-b-0">
                <span className="min-w-[28px] text-[13px] text-gray-500">{item.quantity}x</span>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-800">{item.title}</span>
                  <span className="block text-xs text-gray-400">{item.format_name}</span>
                </div>
                <span className="whitespace-nowrap text-sm text-gray-800">
                  ${item.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>

          {/* Discount code input */}
          {showDiscountInput && (
            <DiscountCodeInput
              initialCode={cart.discountCode ?? null}
              discountAmount={cart.discount ?? 0}
              discountError={discountError}
              discountInfo={cart.discountInfo ?? null}
              isLoading={isLoadingDiscount}
              onApplyDiscount={onApplyDiscount}
              onPreviewDiscount={onPreviewDiscount}
              onConfirmDiscount={onConfirmDiscount}
              onCancelPreview={onCancelPreview}
              previewData={previewData ?? null}
              onClearDiscount={onClearDiscount}
            />
          )}

          <div className="mb-3 rounded-lg bg-gray-50 p-3">
            {cart.subtotal != null && (
              <div className="flex justify-between py-1 text-[13px] text-gray-500">
                <span>Subtotal</span>
                <span>${cart.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {(cart.subscriptionDiscount ?? 0) > 0 && (
              <div className="flex justify-between py-1 text-[13px] text-green-500">
                <span>{cart.subscriptionPlanName || 'Suscripcion'}</span>
                <span>-${(cart.subscriptionDiscount!).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {(cart.discount ?? 0) > 0 && !cart.discountInfo?.is_free_delivery && (
              <div className="flex justify-between py-1 text-[13px] text-green-500">
                <span>{cart.discountInfo?.display_name || (cart.discountCode ? `Cupon: ${cart.discountCode}` : 'Cupon')}</span>
                <span>-${(cart.discount!).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {wholesaleSurcharge > 0 && (
              <div className="flex justify-between py-1 text-[13px] text-gray-700">
                <span>Recargo Mercado Pago mayoristas</span>
                <span>+${wholesaleSurcharge.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {(cart.replacementCreditDiscount ?? 0) > 0 && (
              <div className="flex justify-between py-1 text-[13px] text-green-500">
                <span>Credito por reposicion</span>
                <span>-${(cart.replacementCreditDiscount!).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div className="flex justify-between py-1 text-[13px]">
              <span className={cart.discountInfo?.is_free_delivery ? 'text-green-600 font-medium' : 'text-gray-500'}>
                {cart.discountInfo?.is_free_delivery ? '🚚 Envío gratis' : 'Envio'}
              </span>
              <span className={cart.discountInfo?.is_free_delivery ? 'text-green-600' : 'text-gray-500'}>
                {cart.discountInfo?.is_free_delivery ? (
                  <>
                    <span className="line-through text-gray-400 mr-2">
                      ${cart.deliveryPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="font-medium">Gratis</span>
                  </>
                ) : cart.deliveryPrice > 0 ? (
                  `$${cart.deliveryPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                ) : (
                  'Gratis'
                )}
              </span>
            </div>
            <div className="mt-2 flex justify-between border-t border-gray-200 pt-2.5 text-[15px] font-semibold text-gray-800">
              <span>Total</span>
              <span>${grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <a href={productsPageUrl} className="block text-center text-sm text-green-500 hover:underline">
            Modificar carrito
          </a>
        </div>
      )}
    </div>
  );
}
